import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Avisos } from './agenda';

// CRM → Tu web: cómo va habitania.info. Todo son totales del periodo; la API
// nunca devuelve una visita suelta (ver src/crm_api/web.py).

export type Periodo = 7 | 30 | 90 | 365;

export interface Kpis {
  visitas: number;
  tiempo_medio_s: number | null;
  contactos: number;
  por_cien: number | null;
}

export interface FilaPagina {
  ruta: string;
  nombre: string;
  vistas: number;
  tiempo_medio_s: number | null;
  acciones: Partial<Record<Accion, number>>;
  property_id?: string;
  en_la_web?: boolean;
}

export interface FilaLista {
  clave: string;
  nombre: string;
  visitas: number;
}

export type Accion = 'whatsapp' | 'telefono' | 'correo' | 'me_interesa' | 'guardar';

export interface TuWeb {
  periodo: Periodo;
  /** Primer día medido (AAAA-MM-DD): antes no hay datos. */
  inicio: string;
  /** El dominio aún pide clave: lo medido son pruebas. */
  en_obras: boolean;
  frase: string;
  kpis: Kpis;
  kpis_anterior: Kpis | null;
  /** Por día (AAAA-MM-DD) o, en 12 meses, por mes (AAAA-MM). */
  serie: { etiqueta: string; visitas: number }[];
  fuentes: FilaLista[];
  webs: FilaLista[];
  webs_otras: number;
  campanas: FilaLista[];
  campanas_otras: number;
  paginas: FilaPagina[];
  viviendas: FilaPagina[];
  dispositivos: Partial<Record<'movil' | 'ordenador' | 'tablet' | 'otro', number>>;
  paises: FilaLista[];
  paises_otros: number;
  acciones: Partial<Record<Accion, number>>;
  contactos: {
    web: number;
    cuentas: number;
    estudios: number;
    portal: number;
    por_tipo: { nombre: string; n: number }[];
    por_pagina: { nombre: string; n: number }[];
  };
}

export function useTuWeb(periodo: Periodo) {
  return useQuery<TuWeb>({
    queryKey: ['tu-web', periodo],
    queryFn: async () => (await api.get('/crm/web', { params: { periodo }, timeout: 30000 })).data,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export interface VisitasVivienda {
  dias: number;
  inicio: string;
  vistas: number;
  tiempo_medio_s: number | null;
  acciones: Partial<Record<Accion, number>>;
}

/** La línea de visitas de la ficha de una vivienda (la ve todo el equipo). */
export function useVisitasVivienda(propertyId: string) {
  return useQuery<VisitasVivienda>({
    queryKey: ['tu-web', 'vivienda', propertyId],
    queryFn: async () => (await api.get(`/crm/web/vivienda/${encodeURIComponent(propertyId)}`)).data,
    staleTime: 5 * 60 * 1000,
  });
}

/** Solo admin: el correo «Tu web esta semana». Misma forma que los avisos de la Agenda. */
export function useCorreoWeb() {
  return useQuery<Avisos>({
    queryKey: ['tu-web-correo'],
    queryFn: async () => (await api.get('/crm/web/correo')).data,
  });
}

export function useGuardarCorreoWeb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: Pick<Avisos, 'activo' | 'destinatarios'>) =>
      (await api.put('/crm/web/correo', a)).data as Avisos,
    onSuccess: (data) => qc.setQueryData(['tu-web-correo'], data),
  });
}

export function useProbarCorreoWeb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/crm/web/correo/probar', null, { timeout: 60000 })).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['tu-web-correo'] }),
  });
}

export const NOMBRE_ACCION: Record<Accion, string> = {
  whatsapp: 'WhatsApp',
  telefono: 'Llamar',
  correo: 'Correo',
  me_interesa: 'Me interesa',
  guardar: 'Guardar',
};

export function tiempo(segundos: number | null | undefined): string {
  if (!segundos) return '—';
  if (segundos < 60) return `${segundos} s`;
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return s ? `${m} min ${String(s).padStart(2, '0')} s` : `${m} min`;
}
